import { STUDIO, studioTabs, studioLink, studioArrow, studioButton } from './studio-ui';
import { BRUSHES, ownedBrushes, brushDefinition } from '../shared/art/brushes';
import { brushPreview } from './brush-preview';
import type { CanvasView } from './canvas-view';
import type { ClientHost } from './mode';
import { C, T } from './paint';
import { drawBrushSample } from './brush-sample';
import { PALETTES } from '../shared/account';
import { BRUSH_SIZES, brushIndexForWidth } from '../shared/brush';
import { STROKE_BRUSHES, STROKE_BRUSH_NAMES, STROKE_SHAPE_NAMES, STROKE_SHAPES } from '../shared/art/drawing';
import type { StrokeBrush, StrokeShape } from '../shared/art/types';

/** Both drawing modes use the original draw/guess tools; transport stays with each mode. */
export interface DrawingToolState {
  color: string; eraser: boolean; width: number; eraserWidth: number;
  brush: StrokeBrush; brushWidths: Record<StrokeBrush, number>;
  shape: StrokeShape; shapeFilled: boolean; brushPanel: boolean; usedPalette: string;
}
export interface DrawingToolActions {
  palette(): void; undo(): void; redo(): void; clear(): void; fill(): void; cancelLine(): void;
  canUndo: boolean; canRedo: boolean; background?: string; disabled?: boolean; sliding?: boolean;
}
export function drawingToolLayout(y: number, height: number, size: number, width = 390) {
  const tight = height < 175;
  return { y, height, colorY: y + (tight ? 24 : 34), toolY: y + (tight ? 74 : 84),
    sliderY: y + height - (tight ? 26 : 34), sliderRight: Math.round(width - 27 - Math.max(11, 70 * size / 2000 + 6)) };
}

export function renderDrawingTools(host: ClientHost, state: DrawingToolState, y: number, height: number, size: number, actions: DrawingToolActions, prefix = '') {
  const p = host.paint; const scale = size / 1000; const layout = drawingToolLayout(y, height, size, host.W);
  const { colorY, toolY, sliderY, sliderRight } = layout; const disabled = !!actions.disabled;
  const id = (name: string) => prefix + (prefix && name === 'fill-background' ? 'fill' : name);
  const act = (action: () => void) => () => { state.brushPanel = false; action(); };
  const activeWidth = state.eraser ? state.eraserWidth : state.width;
  const toolButton=(name:string,label:string,icon:import('./paint').IconName,x:number,action:()=>void,active=false,off=false)=>{x=host.x(x);p.box(x-20,toolY-19,40,38,active?STUDIO.pale:STUDIO.surface,12);p.icon(icon,x,toolY,18,off?'#C6CAC9':active?STUDIO.accent:STUDIO.dark);p.hits.push({id:id(name),label,x:x-24,y:toolY-24,w:48,h:48,disabled:off,action});};
  p.box(24,y,host.W-48,height,STUDIO.surface,20,STUDIO.line,7);
  const palettes = PALETTES.filter(palette => (host.player?.palettes ?? ['starter']).includes(palette.id));
  const selected = palettes.find(palette => palette.id === state.usedPalette) ?? palettes[0] ?? PALETTES[0];
  state.usedPalette = selected.id;
  const colorLeft=32,colorRight=host.W-116,step=(colorRight-colorLeft)/selected.colors.length;
  selected.colors.forEach((color, i) => {
    const cx=colorLeft+(i+.5)*step,active=state.color===color&&!state.eraser;
    p.dab(cx,colorY,Math.min(active?11:9,step/2-2),color,active);
    p.hits.push({ id: id(`color-${i}`), label: `颜色 ${i + 1}`, x:colorLeft+i*step,y:colorY-22,w:step,h:44,
      disabled, action: act(() => { state.color = color; state.eraser = false; host.drawingSettings.color(color); }) });
  });
  studioLink(p,id('palette'),'更多',host.W-100,colorY-24,66,48,act(actions.palette),disabled);
  toolButton('undo','撤销','undo',57,act(actions.undo),false,disabled || !actions.canUndo);
  toolButton('redo','重做','redo',112,act(actions.redo),false,disabled || !actions.canRedo);
  p.box(host.x(167)-21,toolY-19,42,38,STUDIO.pale,12,state.brushPanel?STUDIO.accent:undefined);
  if(state.eraser)drawBrushSample(p.ctx,'pen',host.x(167),toolY,17,activeWidth*scale,C.mute);
  else { p.ctx.save();p.ctx.beginPath();p.ctx.arc(host.x(167),toolY,16,0,Math.PI*2);p.ctx.clip();brushPreview(host.platform,p.ctx,state.brush,state.color,host.drawingSettings.settings(state.brush),host.x(167)-16,toolY-10,32,20);p.ctx.restore(); }
  p.hits.push({ id: id('brush'), label: `粗细 ${activeWidth} · ${state.eraser ? '橡皮' : STROKE_BRUSH_NAMES[state.brush]}`, x: host.x(167)-24, y: toolY - 24, w: 48, h: 48, disabled,
    action: () => { state.brushPanel = !state.brushPanel; actions.cancelLine(); } });
  toolButton('eraser','橡皮','eraser',223,act(()=>{state.eraser=!state.eraser;actions.cancelLine();}),state.eraser,disabled);
  toolButton('clear','清空','clear',278,act(actions.clear),false,disabled);
  toolButton('fill-background','油漆桶','fill',333,act(actions.fill),false,disabled||actions.background===state.color);
  p.circle(host.x(333)+9,toolY+12,4,state.color,STUDIO.surface,0);
  // Match the selected nominal diameter on the untransformed drawing viewport.
  p.circle(56,sliderY,17,STUDIO.muted);
  p.circle(56,sliderY,Math.max(.5,activeWidth*scale/2),state.eraser?STUDIO.surface:state.color,state.eraser||state.color.toUpperCase()==='#FFFFFF'?STUDIO.quiet:undefined);
  const knobX=94+(sliderRight-94)*(activeWidth-1)/69;
  p.line([[94,sliderY],[sliderRight,sliderY]],STUDIO.line,4);p.line([[94,sliderY],[knobX,sliderY]],STUDIO.accent,4);
  if(actions.sliding){p.box(knobX-18,sliderY-12,36,24,STUDIO.accent,9);p.text(String(Math.round(activeWidth)),knobX,sliderY,T.caption,C.white,'600','center');}
  else {p.circle(knobX,sliderY,9,STUDIO.surface,STUDIO.accent,2);p.circle(knobX,sliderY,3,STUDIO.accent);}
  p.hits.push({id:id('width-slider'),label:`画笔粗细 ${activeWidth}`,x:82,y:sliderY-22,w:sliderRight-70,h:44,disabled,drag:true,action:()=>{},onAdjust:direction=>{host.drawingSettings.width(state,Math.max(1,Math.min(70,activeWidth+direction)));}});
  return layout;
}

export function brushCatalogBox(_boardY: number, toolsY: number) {
  // On short screens grow upward so the catalogue never covers the colors or tools.
  const height = 236;
  return { y: toolsY - height, height };
}
export function renderBrushCatalog(host: ClientHost, state: DrawingToolState, box: { y: number; height: number }, size: number, select: (brush: StrokeBrush) => void, prefix = '') {
  const p=host.paint, y=box.y, studio=host.studio, owned=ownedBrushes(host.player);
  p.box(24,y,host.W-48,box.height,STUDIO.surface,20,STUDIO.line,14);
  studioTabs(p,(['recent','favorites'] as const).map(tab=>({id:`${prefix}quick-${tab}`,label:tab==='recent'?'最近':'收藏',action:()=>{state.brushPanel=true;studio.quickTab=tab;studio.quickPage=0;}})),studio.quickTab==='recent'?0:1,36,y+2,150,54);
  const list=studio.quickTab==='favorites'?host.drawingSettings.value.favorites.filter(b=>owned.includes(b)):[...new Set([...host.drawingSettings.value.recent,...owned])];
  const pages=Math.max(1,Math.ceil(list.length/8));studio.quickPage=Math.min(studio.quickPage,pages-1);
  p.text(`${studio.quickPage+1} / ${pages}`,host.W-100,y+29,T.caption,STUDIO.quiet,'500','center');
  studioArrow(p,`${prefix}quick-page`,host.W-84,y+2,54,1,()=>{state.brushPanel=true;studio.quickPage=(studio.quickPage+1)%pages;},pages===1);
  list.slice(studio.quickPage*8,studio.quickPage*8+8).forEach((brush,i)=>{
    const cell=host.tablet?(host.W-64)/4:83,w=cell-7,x=32+i%4*cell,by=y+56+Math.floor(i/4)*63,active=state.brush===brush&&!state.eraser;
    p.box(x,by,w,57,active?STUDIO.pale:STUDIO.surface,12,active?'#BAD7E9':undefined);
    brushPreview(host.platform,p.ctx,brush,active?STUDIO.accent:STUDIO.dark,host.drawingSettings.settings(brush),x+5,by+2,w-10,32);
    p.fit(brushDefinition(brush).name,x+w/2,by+44,w-6,T.note,active?STUDIO.accent:STUDIO.dark,active?'700':'500','center');
    p.hits.push({id:prefix?`${prefix}select-brush-${brush}`:`brush-${brush}`,label:`笔头 ${brushDefinition(brush).name}`,x,y:by,w,h:57,action:()=>{state.brushPanel=false;select(brush);}});
  });
  if(!list.length)p.text('把喜欢的笔刷收藏在这里',195,y+116,T.sub,STUDIO.quiet,'400','center');
  p.line([[40,y+181],[host.W-40,y+181]],STUDIO.line,1);
  studioLink(p,`${prefix}all-brushes`,'浏览全部画笔',32,y+182,(host.W-76)/2,54,()=>{state.brushPanel=false;host.studio.open(state.brush);});
  studioLink(p,`${prefix}brush-settings`,'调节当前笔',host.W/2+6,y+182,(host.W-76)/2,54,()=>{state.brushPanel=false;host.studio.open(state.brush,'settings');});

}

export function snappedLine(from: { x: number; y: number }, to: { x: number; y: number }) {
  const dx = to.x - from.x; const dy = to.y - from.y; const length = Math.hypot(dx, dy);
  if (length < 1) return to;
  const step = Math.PI / 4; const angle = Math.round(Math.atan2(dy, dx) / step) * step;
  if (Math.abs(Math.atan2(dy, dx) - angle) > 8 * Math.PI / 180) return to;
  return { x: from.x + Math.cos(angle) * length, y: from.y + Math.sin(angle) * length };
}

export function selectDrawingBrush(host: ClientHost, state: DrawingToolState, brush: StrokeBrush, changed: () => void) {
  if(!host.drawingSettings.select(brush)){ host.studio.open(brush,'settings'); return; }
  state.brushWidths[state.brush] = state.width;
  state.brush = brush; state.width = state.brushWidths[brush];
  state.eraser = false; changed();
  if (!host.platform.get(`brushTip:${brush}`, false)) {
    host.platform.set(`brushTip:${brush}`, true);
    const tips: Partial<Record<StrokeBrush, string>> = {
      pen: '钢笔：最普通的实心笔画。',
      translucent: '半透明：叠在一起会变深，上阴影和腮红很好用。',
      line: '直线：拖动预览、抬手落笔；接近水平 / 垂直 / 45° 会自动吸直。',
      outline: '镂空：像空心字一样只留一圈颜色，中间是纸色。',
      marker: '荧光笔：又粗又淡，适合涂大块颜色和高亮。',
      crayon: '蜡笔：边缘有颗粒感，涂色更像手绘。',
      mirror: '对称笔：一边画，另一边同时画出来，画脸和蝴蝶特别快。',
      dash: '虚线：点线 / 断线，画路径、边界和马路很好用。',
      spray: '喷枪：颗粒喷雾，适合星空、沙滩和毛茸茸的轮廓。',
      glow: '光晕：笔画带一圈柔光，在深色背景上像霓虹灯。',
      ink: '毛笔：慢慢画更粗、快笔收细，写大字和画竹叶很有味道。',
      shape: `形状：拖出一个${STROKE_SHAPE_NAMES[state.shape]}，抬手落笔；在「调节当前笔」中选择圆、方、三角及空心实心。`,
    };
    host.toast(tips[brush] ?? '在画笔册中可试画和调节这支笔。');
  }
}

export function renderDragPreview(host: ClientHost, state: DrawingToolState, board: { x: number; y: number; size: number }, view: CanvasView, fromPoint: { x: number; y: number }, toPoint: { x: number; y: number }, frozen?: import('../shared/art/drawing').StrokeOptions) {
  if(frozen){const brush=frozen.brush??state.brush;state={...state,brush,color:frozen.color,brushWidths:{...state.brushWidths,[brush]:frozen.width},shape:frozen.shape??state.shape,shapeFilled:frozen.shapeFilled??state.shapeFilled};}
  const b = board; const unit = b.size * view.zoom / 1000;
  const to = state.brush === 'line' ? snappedLine(fromPoint, toPoint) : toPoint;
  const from = fromPoint; const end = to;
  const c = host.ctx; const width = Math.max(1.5 / unit, state.brushWidths[state.brush]);
  c.save(); view.apply(c, b);
  c.beginPath(); c.rect(0, 0, 1000, 1000); c.clip();
  c.strokeStyle = state.color; c.globalAlpha = 0.75; c.lineWidth = width; c.lineCap = 'round'; c.lineJoin = 'round';
  if (state.brush === 'shape') {
    // 预览按与渲染完全相同的几何：包围盒两点，圆取短边。
    const left = Math.min(from.x, end.x); const right = Math.max(from.x, end.x);
    const top = Math.min(from.y, end.y); const bottom = Math.max(from.y, end.y);
    const cx = (left + right) / 2; const cy = (top + bottom) / 2; const half = Math.min(right - left, bottom - top) / 2;
    c.setLineDash([6 / unit, 5 / unit]);
    c.beginPath();
    if (state.shape === 'rect') c.rect(left, top, right - left, bottom - top);
    else if (state.shape === 'triangle') { c.moveTo(cx, cy - half); c.lineTo(cx + half, cy + half); c.lineTo(cx - half, cy + half); c.closePath(); }
    else c.arc(cx, cy, Math.max(1, half), 0, Math.PI * 2);
    if (state.shapeFilled) { c.globalAlpha = 0.28; c.fillStyle = state.color; c.fill(); c.globalAlpha = 0.75; }
    c.stroke();
  } else {
    if (!(Math.hypot(to.x - toPoint.x, to.y - toPoint.y) > 0.5)) c.setLineDash([6 / unit, 5 / unit]);
    c.beginPath(); c.moveTo(from.x, from.y); c.lineTo(end.x, end.y); c.stroke();
  }
  c.setLineDash([]); c.globalAlpha = 1;
  for (const point of [from, end]) { c.beginPath(); c.arc(point.x, point.y, Math.max(2.5 / unit, width * 0.6), 0, Math.PI * 2); c.fillStyle = C.white; c.fill(); c.lineWidth = 2 / unit; c.strokeStyle = state.color; c.stroke(); }
  c.restore();
}
